import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Chip,
  InputAdornment,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Divider,
  Autocomplete,
} from '@mui/material';
import {
  Check as ApproveIcon,
  Close as RejectIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Redeem as RedeemIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  EmojiEvents as RewardIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { rewardService } from '../../services/reward';
import { CircularProgress } from '@mui/material';

const RedeemRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [actionRemarks, setActionRemarks] = useState('');
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [dialogMessage, setDialogMessage] = useState('');

  const mapApiRequestToLocal = (apiRequest) => {
    const associate = apiRequest.associate || apiRequest.associateId || {};
    const reward = apiRequest.reward || apiRequest.rewardId || {};
    
    return {
      id: apiRequest._id || apiRequest.id,
      requestId: apiRequest.requestId ? `REQ-${apiRequest.requestId}` : 'N/A',
      customerId: associate.associateId ? `ASSOC-${associate.associateId}` : associate._id || 'N/A',
      customerName: associate.name || 'N/A',
      customerEmail: associate.email || 'N/A',
      customerPhone: associate.phone || 'N/A',
      rewardId: reward.rewardId ? `RWD-${reward.rewardId}` : reward._id || 'N/A',
      rewardName: reward.title || 'N/A',
      pointsRedeemed: reward.redeemPoints || 0,
      requestDate: apiRequest.createdAt || new Date().toISOString(),
      status: apiRequest.requestStatus === 'pending' ? 'Pending' : 
              apiRequest.requestStatus === 'approved' ? 'Approved' : 
              apiRequest.requestStatus === 'rejected' ? 'Rejected' : 'Pending',
      remarks: apiRequest.remarks || '',
      associateData: associate,
      rewardData: reward,
    };
  };

  const fetchRedeemRequests = async () => {
    try {
      setLoading(true);
      // Always fetch pending requests only
      const data = await rewardService.getRedeemRequests('pending');

      const mappedRequests = Array.isArray(data.data) ? data.data.map(mapApiRequestToLocal) : [];
      setRequests(mappedRequests);
    } catch (err) {
      console.error('Failed to fetch redeem requests:', err);
      setRequests([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRedeemRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleApprove = (request) => {
    setSelectedRequest(request);
    setActionRemarks('');
    setApproveDialogOpen(true);
  };

  const handleReject = (request) => {
    setSelectedRequest(request);
    setActionRemarks('');
    setRejectDialogOpen(true);
  };

  const handleConfirmApprove = async () => {
    if (!selectedRequest) return;

    try {
      setApproving(true);
      const response = await rewardService.approveRedeemRequest(selectedRequest.id);
      
      if (response.status === true || response.success === true) {
        setApproveDialogOpen(false);
        setSelectedRequest(null);
        setActionRemarks('');
        setSuccessDialogOpen(true);
        setDialogMessage(response.message || 'Request approved successfully!');
        // Refresh the requests list
        fetchRedeemRequests();
      } else {
        throw new Error(response.message || 'Failed to approve request');
      }
    } catch (err) {
      setErrorDialogOpen(true);
      setDialogMessage(err?.message || 'Failed to approve request');
    } finally {
      setApproving(false);
    }
  };

  const handleConfirmReject = async () => {
    if (!selectedRequest) return;

    if (!actionRemarks || actionRemarks.trim() === '') {
      return; // Validation: rejection reason is required
    }

    try {
      setRejecting(true);
      const response = await rewardService.rejectRedeemRequest(selectedRequest.id);
      
      if (response.status === true || response.success === true) {
        setRejectDialogOpen(false);
        setSelectedRequest(null);
        setActionRemarks('');
        setSuccessDialogOpen(true);
        setDialogMessage(response.message || 'Request rejected successfully!');
        // Refresh the requests list
        fetchRedeemRequests();
      } else {
        throw new Error(response.message || 'Failed to reject request');
      }
    } catch (err) {
      setErrorDialogOpen(true);
      setDialogMessage(err?.message || 'Failed to reject request');
    } finally {
      setRejecting(false);
    }
  };

  const handleView = (request) => {
    setSelectedRequest(request);
    setViewDialogOpen(true);
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case 'Pending':
        return { backgroundColor: '#fff3e0', color: '#f57c00' };
      case 'Approved':
        return { backgroundColor: '#e8f5e9', color: '#2e7d32' };
      case 'Rejected':
        return { backgroundColor: '#ffebee', color: '#c62828' };
      default:
        return { backgroundColor: '#f5f5f5', color: '#666' };
    }
  };

  // Get unique customer suggestions for autocomplete
  const customerMap = new Map();
  requests.forEach((req) => {
    const key = `${req.customerId}-${req.customerPhone}`;
    if (!customerMap.has(key)) {
      customerMap.set(key, {
        label: `${req.customerName} - ${req.customerPhone}`,
        name: req.customerName,
        phone: req.customerPhone,
        customerId: req.customerId,
      });
    }
  });
  const customerSuggestions = Array.from(customerMap.values());

  const filteredRequests = requests.filter((request) => {
    const matchesSearch =
      searchQuery === '' ||
      request.requestId.toLowerCase().includes(searchQuery.toLowerCase()) ||
      request.customerName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      request.customerEmail.toLowerCase().includes(searchQuery.toLowerCase()) ||
      request.customerPhone.toLowerCase().includes(searchQuery.toLowerCase()) ||
      request.rewardName.toLowerCase().includes(searchQuery.toLowerCase());
    // Only show pending requests
    return matchesSearch && request.status === 'Pending';
  });

  return (
    <Box sx={{ p: 3 }}>
      {/* Header Section */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <RedeemIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" fontWeight="bold" color="primary">
            Redeem Requests
          </Typography>
        </Box>
        <Button 
          variant="outlined" 
          startIcon={<RefreshIcon />} 
          onClick={fetchRedeemRequests}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {/* Search and Filter Section */}
      <Card sx={{ mb: 3, boxShadow: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <Autocomplete
                freeSolo
                options={customerSuggestions}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') return option;
                  return option.label || `${option.name} - ${option.phone}`;
                }}
                inputValue={searchQuery}
                onInputChange={(event, newInputValue) => {
                  setSearchQuery(newInputValue);
                  setPage(0);
                }}
                onChange={(event, newValue) => {
                  if (newValue && typeof newValue === 'object') {
                    setSearchQuery(newValue.name || newValue.label);
                  }
                  setPage(0);
                }}
                filterOptions={(options, { inputValue }) => {
                  return options.filter((option) => {
                    const search = inputValue.toLowerCase();
                    return (
                      option.name.toLowerCase().includes(search) ||
                      option.phone.toLowerCase().includes(search) ||
                      option.customerId.toLowerCase().includes(search) ||
                      option.label.toLowerCase().includes(search)
                    );
                  });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search by Customer Name or Phone Number"
                    variant="outlined"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props} key={option.customerId}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {option.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.phone} • {option.customerId}
                      </Typography>
                    </Box>
                  </Box>
                )}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Table Section */}
      <Paper sx={{ borderRadius: 2, boxShadow: 3, overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400, p: 4 }}>
            <CircularProgress size={50} />
          </Box>
        ) : (
          <TableContainer>
            <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>S.No</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Request ID</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Customer Name</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Reward</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Points Redeemed</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Request Date</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }} align="center">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRequests.length > 0 ? (
                filteredRequests
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((request, index) => (
                    <TableRow key={request.id} hover>
                      <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                      <TableCell>
                        <Typography fontWeight="medium" color="primary">
                          {request.requestId}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {request.customerName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {request.customerId}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {request.rewardName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {request.rewardId}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium" color="warning.main">
                          {request.pointsRedeemed.toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {new Date(request.requestDate).toLocaleDateString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={request.status}
                          size="small"
                          style={getStatusStyle(request.status)}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          <Tooltip title="View Details">
                            <IconButton color="info" size="small" onClick={() => handleView(request)}>
                              <InfoIcon />
                            </IconButton>
                          </Tooltip>
                          {request.status === 'Pending' && (
                            <>
                              <Tooltip title="Approve">
                                <IconButton
                                  color="success"
                                  size="small"
                                  onClick={() => handleApprove(request)}
                                >
                                  <ApproveIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Reject">
                                <IconButton
                                  color="error"
                                  size="small"
                                  onClick={() => handleReject(request)}
                                >
                                  <RejectIcon />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="textSecondary">
                      No redemption requests found.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        )}

        {/* Pagination */}
        {!loading && (
          <TablePagination
            // rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={filteredRequests.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        )}
      </Paper>

      {/* View Details Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <InfoIcon sx={{ color: 'primary.main' }} />
            Request Details - {selectedRequest?.requestId}
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedRequest && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Request ID
                </Typography>
                <Typography variant="body1" sx={{ mb: 2, fontWeight: 600 }}>
                  {selectedRequest.requestId}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Status
                </Typography>
                <Chip
                  label={selectedRequest.status}
                  size="small"
                  style={getStatusStyle(selectedRequest.status)}
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                  Customer Information
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Customer Name
                </Typography>
                <Typography variant="body1" sx={{ mb: 2, fontWeight: 600 }}>
                  {selectedRequest.customerName}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Customer ID
                </Typography>
                <Typography variant="body1" sx={{ mb: 2, fontWeight: 600 }}>
                  {selectedRequest.customerId}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Email
                </Typography>
                <Typography variant="body1" sx={{ mb: 2, fontWeight: 600 }}>
                  {selectedRequest.customerEmail}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Phone
                </Typography>
                <Typography variant="body1" sx={{ mb: 2, fontWeight: 600 }}>
                  {selectedRequest.customerPhone}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                  Reward Information
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Reward Name
                </Typography>
                <Typography variant="body1" sx={{ mb: 2, fontWeight: 600 }}>
                  {selectedRequest.rewardName}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Reward ID
                </Typography>
                <Typography variant="body1" sx={{ mb: 2, fontWeight: 600 }}>
                  {selectedRequest.rewardId}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Points Redeemed
                </Typography>
                <Typography variant="body1" sx={{ mb: 2, fontWeight: 600, color: 'warning.main' }}>
                  {selectedRequest.pointsRedeemed.toLocaleString()}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Request Date
                </Typography>
                <Typography variant="body1" sx={{ mb: 2, fontWeight: 600 }}>
                  {new Date(selectedRequest.requestDate).toLocaleDateString()}
                </Typography>
              </Grid>
              {selectedRequest.remarks && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Remarks
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {selectedRequest.remarks}
                  </Typography>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Approve Dialog */}
      <Dialog open={approveDialogOpen} onClose={() => setApproveDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Approve Redemption Request</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Are you sure you want to approve this redemption request?
          </Alert>
          {selectedRequest && (
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Request ID:</strong> {selectedRequest.requestId}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Customer:</strong> {selectedRequest.customerName}
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                <strong>Reward:</strong> {selectedRequest.rewardName} ({selectedRequest.pointsRedeemed} points)
              </Typography>
              <TextField
                fullWidth
                label="Remarks (optional)"
                multiline
                rows={3}
                value={actionRemarks}
                onChange={(e) => setActionRemarks(e.target.value)}
                placeholder="Enter approval remarks..."
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setApproveDialogOpen(false)} 
            disabled={approving}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            color="success" 
            onClick={handleConfirmApprove}
            disabled={approving}
            startIcon={approving ? <CircularProgress size={18} color="inherit" /> : <ApproveIcon />}
          >
            {approving ? 'Approving...' : 'Approve'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Redemption Request</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Are you sure you want to reject this redemption request?
          </Alert>
          {selectedRequest && (
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Request ID:</strong> {selectedRequest.requestId}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Customer:</strong> {selectedRequest.customerName}
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                <strong>Reward:</strong> {selectedRequest.rewardName} ({selectedRequest.pointsRedeemed} points)
              </Typography>
              <TextField
                fullWidth
                label="Rejection Reason *"
                multiline
                rows={3}
                value={actionRemarks}
                onChange={(e) => setActionRemarks(e.target.value)}
                required
                placeholder="Enter rejection reason..."
                error={!actionRemarks}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setRejectDialogOpen(false)} 
            disabled={rejecting}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmReject}
            disabled={!actionRemarks || rejecting}
            startIcon={rejecting ? <CircularProgress size={18} color="inherit" /> : <RejectIcon />}
          >
            {rejecting ? 'Rejecting...' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Dialog */}
      <Dialog open={successDialogOpen} onClose={() => setSuccessDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircleIcon sx={{ color: 'success.main', fontSize: 28 }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Success
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">{dialogMessage}</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setSuccessDialogOpen(false)}
            sx={{ textTransform: 'none' }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Error Dialog */}
      <Dialog open={errorDialogOpen} onClose={() => setErrorDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ErrorIcon sx={{ color: 'error.main', fontSize: 28 }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Error
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">{dialogMessage}</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            color="error"
            onClick={() => setErrorDialogOpen(false)}
            sx={{ textTransform: 'none' }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RedeemRequests;

